import type { ListMetadata, ListItem, ConvertSearchToListParams } from "$lib/types/list";

export class ListAPI {
  private static readonly BASE_URL = "/api/v1/lists";
  private static readonly ENDPOINTS = {
    getUserLists: "/get_user_lists",
    createList: "",
    getList: "/:id",
    updateList: "/:id",
    deleteList: "/",
    updateVisibility: "/:id/visibility",
    getListItems: "/:id/items",
    addItems: "/:id/items",
    deleteItem: "/:id/items/:candidate_id",
    listMetadataSlug: "/get_list_metadata_slug",
    listMetadataSlugAuth: "/get_list_metadata_slug_auth",
    listItemsSlug: "/get_list_items_by_slug",
    listItemsSlugAuth: "/get_list_items_by_slug_auth",
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  /**
   * Gets a list by ID
   * @throws Error if the fetch request fails
   */
  static async getList(id: number): Promise<ListMetadata> {
    try {
      const response = await fetch(`${this.BASE_URL}/${id}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("List not found");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.validateAndNormalizeListMetadata(data);
    } catch (error) {
      console.error("Get list error:", error);
      throw new Error(`Failed to get list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Gets all lists for a user
   * @throws Error if the fetch request fails
   */
  static async getUserLists(): Promise<ListMetadata[]> {
    try {
      logger.log("getUserLists");
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.getUserLists}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({}),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          logger.log("Authentication required to fetch user lists");
          throw new Error("Authentication required");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: unknown) => this.validateAndNormalizeListMetadata(item));
    } catch (error) {
      console.error("Get user lists error:", error);
      throw new Error(`Failed to get user lists: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Deletes a list
   * @param id The list ID
   * @param slug The list slug
   * @throws Error if the fetch request fails or if unauthorized
   */
  static async deleteList(id: number, slug: string): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL}/${id}`, {
        method: "DELETE",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ slug }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 404) {
          throw new Error("List not found");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Delete list error:", error);
      throw new Error(`Failed to delete list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Creates a new list
   * @throws Error if the fetch request fails
   */
  static async createList(params: { title: string; isPublic: boolean }): Promise<ListMetadata> {
    try {
      const response = await fetch(this.BASE_URL, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          title: params.title,
          is_public: params.isPublic,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.validateAndNormalizeListMetadata(data);
    } catch (error) {
      console.error("Create list error:", error);
      throw new Error(`Failed to create list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Gets paginated items in a list
   * @throws Error if the fetch request fails
   */
  static async getListItems(params: { listId: number; limit: number; offset: number }): Promise<ListItem[]> {
    try {
      const response = await fetch(
        `${this.BASE_URL}/${params.listId}/items?limit=${params.limit}&offset=${params.offset}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("List not found");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: unknown) => item);
    } catch (error) {
      console.error("Get list items error:", error);
      throw new Error(`Failed to get list items: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Updates list visibility
   * @throws Error if the fetch request fails
   */
  static async updateListVisibility(params: { id: number; isPublic: boolean }): Promise<ListMetadata> {
    try {
      const response = await fetch(`${this.BASE_URL}/${params.id}/visibility`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          is_public: params.isPublic,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 404) {
          throw new Error("List not found");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.validateAndNormalizeListMetadata(data);
    } catch (error) {
      console.error("Update list visibility error:", error);
      throw new Error(`Failed to update list visibility: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Adds items to a list
   * @throws Error if the fetch request fails
   */
  static async addItemsToList(params: { listId: number; candidateIds: number[] }): Promise<ListItem[]> {
    try {
      const response = await fetch(`${this.BASE_URL}/${params.listId}/items`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          candidate_ids: params.candidateIds,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 404) {
          throw new Error("List not found");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: unknown) => item);
    } catch (error) {
      console.error("Add items to list error:", error);
      throw new Error(`Failed to add items to list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Gets list metadata by slug
   * @throws Error if the fetch request fails
   */
  static async getListMetadataBySlug(params: {
    slug: string;
    auth?: boolean;
    userId?: string;
    publicFallback?: boolean;
  }): Promise<ListMetadata> {
    try {
      const endpoint = params.auth ? this.ENDPOINTS.listMetadataSlugAuth : this.ENDPOINTS.listMetadataSlug;

      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          slug: params.slug,
          user_id: params.userId ? Number(params.userId) : undefined,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (params.publicFallback && params.auth) {
          logger.log("Falling back to public access for list:", params.slug);
          return await this.getListMetadataBySlug({
            slug: params.slug,
            auth: false,
            userId: params.userId,
          });
        }

        if (response.status === 404) {
          throw new Error("List not found");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 401) {
          throw new Error("Authentication required");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.validateAndNormalizeListMetadata(data);
    } catch (error) {
      console.error("Get list metadata by slug error:", error);
      throw new Error(`Failed to get list metadata: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Deletes an item from a list
   * @throws Error if the fetch request fails
   */
  static async deleteItemFromList(params: { listId: number; candidateId: number }): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL}/${params.listId}/items/${params.candidateId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 404) {
          throw new Error("List or item not found");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Delete item from list error:", error);
      throw new Error(`Failed to delete item from list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  private static validateAndNormalizeListMetadata(data: any): ListMetadata {
    if (!data || typeof data !== "object") {
      throw new Error("List response must be an object");
    }

    logger.log("Validating list metadata:", data);
    return {
      id: this.validateNumber(data.id, "id"),
      userId: this.validateNumber(data.user_id, "userId"),
      title: this.validateString(data.title, "title"),
      slug: this.validateString(data.slug, "slug"),
      isPublic: this.validateBoolean(data.is_public, "isPublic"),
      createdAt: new Date(this.validateString(data.created_at, "createdAt")),
      updatedAt: new Date(this.validateString(data.updated_at, "updatedAt")),
    };
  }

  /**
   * Gets paginated items for a list using its slug
   * @throws Error if the fetch request fails
   */
  static async getListResultsBySlug(
    slug: string,
    limit: number,
    offset: number,
    auth?: boolean,
    userId?: string,
    publicFallback?: boolean,
  ): Promise<ListItem[]> {
    try {
      const endpoint = auth ? this.ENDPOINTS.listItemsSlugAuth : this.ENDPOINTS.listItemsSlug;

      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          slug,
          limit,
          offset,
          user_id: userId ? Number(userId) : undefined,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (publicFallback && auth) {
          logger.log("Falling back to public access for list items:", slug);
          return await this.getListResultsBySlug(slug, limit, offset, false, userId);
        }

        if (response.status === 404) {
          throw new Error("List not found");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to list");
        } else if (response.status === 401) {
          throw new Error("Authentication required");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: unknown) => item);
    } catch (error) {
      console.error("Get list items error:", error);
      throw new Error(`Failed to get list items: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async getListItemCandidateIds(listId: string): Promise<number[]> {
    try {
      const response = await fetch(`${this.BASE_URL}/${listId}/candidate-ids`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ list_id: listId }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.candidate_ids;
    } catch (error) {
      console.error("Failed to get list item candidate IDs:", error);
      throw new Error(
        `Failed to get list item candidate IDs: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  private static validateString(value: unknown, fieldName: string, defaultValue = ""): string {
    if (!value) return defaultValue;
    if (typeof value !== "string") {
      throw new Error(`${fieldName} must be a string`);
    }
    return value;
  }

  private static validateNumber(value: unknown, fieldName: string, defaultValue = 0): number {
    if (!value) return defaultValue;
    if (typeof value !== "number") {
      throw new Error(`${fieldName} must be a number`);
    }
    return value;
  }

  private static validateBoolean(value: unknown, fieldName: string, defaultValue = false): boolean {
    if (value === undefined || value === null) return defaultValue;
    if (typeof value !== "boolean") {
      throw new Error(`${fieldName} must be a boolean`);
    }
    return value;
  }

  // Add this method to your existing ListAPI class

  /**
   * Converts a search to a list
   * @throws Error if the fetch request fails
   */
  static async convertSearchToList(params: ConvertSearchToListParams): Promise<ListMetadata> {
    try {
      logger.log("Converting search with params:", params);
      logger.log("Request URL:", `${this.BASE_URL}/convert-search`);
      const response = await fetch(`${this.BASE_URL}/convert-search`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          search_id: params.searchId,
          title: params.title,
          is_public: false, // Default to private list
        }),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        } else if (response.status === 403) {
          throw new Error("Unauthorized access to search");
        } else if (response.status === 404) {
          throw new Error("Search not found");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.validateAndNormalizeListMetadata(data);
    } catch (error) {
      console.error("Convert search to list error:", error);
      throw new Error(`Failed to convert search to list: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }
}
